import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, Subject } from 'rxjs';
import { DocumentModel } from '../models/document.model';
import { ObjectModel } from '../models/object.model';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { MaskitoOptions } from '@maskito/core';
import { ElementState } from '@maskito/core/src/lib/types';


@Injectable({
  providedIn: 'root',
})
export class MaskitoService {

  constructor() {}

  public createMaskitoMask(mask: string): MaskitoOptions {
    //console.log(`creating mask from picture - ${mask}`);
    let maskRegexArray: (string | RegExp)[] = [];
    if (mask !== '' && !mask.includes('@')) {
      if (mask === '##/##/##') {
        return this.createMaskitoDateOptions(1);
      } else if (mask === '##/##/####' ) {
        return this.createMaskitoDateOptions(2);
      } else {
        mask.split('').map(char => {
          if (char === '#') { //allow numeric inputs only
            maskRegexArray.push(/\d/);
          } else if (char === '?') { //allow any input in place of "?"
            maskRegexArray.push(/.*/);
          } else { //push the actual character
            maskRegexArray.push(char);
          }
        });
        return {
          //### if we use the maskitoWithPlaceholder it causes the input (change) to not be fired - leave it off for now - taiga updates package regularly - perhaps we can bring it back
          //...maskitoWithPlaceholder(mask),
          mask: maskRegexArray
        };
      }
    } else if (mask.startsWith('@')) {
        //determine if its a date mask or number mask
        if (mask.toUpperCase().includes('D')) {
            if (mask.includes('1')) {
                return this.createMaskitoDateOptions(1); 
            } else if (mask.includes('2')) {
                return this.createMaskitoDateOptions(2);
            }            
        } else if (mask.toUpperCase().includes('N')) {
            const numberFormatRegex = /@N(-?)(_?)(\d{1,2})(\.?)(\d?)/i;
            const formatExploded = mask.match(numberFormatRegex);
            //shortcut if we can't parse
            if (!formatExploded) {
                return {
                    mask: /.*/
                  };                 
            } else { 
              let allowNegative = !!formatExploded[1];
              let maxLength = Number(formatExploded[3]); 
              let decimalsLength = Number(formatExploded[5]); 
              return this.createMaskitoNumberOptions(allowNegative, maxLength, decimalsLength);
            }
        }
    }
    //fail safe
    return {
        mask: /.*/
    };   
    
  }

  public createMaskitoNumberOptions(allowNegative: boolean, maxLength: number, decimalsLength: number) : MaskitoOptions {
    //console.log('Creating MaskitoNumberMask ',allowNegative,maxLength,decimalsLength);
    if (allowNegative) {
        return maskitoNumberOptionsGenerator({
            minusSign: '-',
            thousandSeparator: ',',
            decimalSeparator: '.',
            precision: decimalsLength,
            min: undefined, //must be undefined to allow negative input
            max: this.getMaxNumberValue(maxLength, decimalsLength)
        })
    } else {
        return maskitoNumberOptionsGenerator({
            thousandSeparator: ',',
            decimalSeparator: '.',
            precision: decimalsLength,
            min: 0,
            max: this.getMaxNumberValue(maxLength, decimalsLength)
        })
    }
  }


  public createMaskitoDateOptions(datePictureType: number) : MaskitoOptions {
    let mask: Array<RegExp | string> = [];

    if (datePictureType === 1) {
        mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/];
    } else if (datePictureType === 2) {
        mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
    } else {
        mask = [/.*/];     
    }

    return {
      mask: mask,
      postprocessors: [
        ({ value, selection }: ElementState) => {
          //default no input to 0 to match TXdocs
          const defaultValue = datePictureType === 1 ? '00/00/00' : '00/00/0000';
          let formattedValue = value.trim().length === 0 ? defaultValue : value;
          const parts = formattedValue.split('/');
          let newCursor = selection[0];
          let [month = '00', day = '00', year = datePictureType === 1 ? '00' : '0000'] = parts;
          
          let numericMonth = parseInt(month, 10);
          let singleDigitMonth = parseInt(month[0], 10);

          if (singleDigitMonth > 1 && singleDigitMonth <= 9) {
            month = `0${singleDigitMonth}`;
            newCursor = month.length + 1; //advance the cursor to behind the padded month
          } else if (numericMonth > 12) {
            month = '12';
          }
  
          const numericYear = parseInt(year, 10);
          const maxDays = this.getMaxDaysInMonth(numericMonth, numericYear);
  
          let numericDay = parseInt(day, 10);
          if (numericDay < 1) day = '00';
          if (numericDay > maxDays) day = maxDays.toString().padStart(2, '0');

          //final formatting
          formattedValue = `${month}/${day}/${year}`;
      
          //manage cursor
          const originalValue = value;
          const originalCursor = selection[0]; 
          

          //handle backspace and replacing with 0
          if (originalValue.length > formattedValue.length) {
            if (originalCursor > 0) {
              const cursorBefore = originalCursor - 1;
              if (formattedValue[cursorBefore] === '/') { //skip seperator
                formattedValue + formattedValue.slice(0, cursorBefore - 1) + '0' + formattedValue.slice(cursorBefore);
                newCursor = cursorBefore - 1;
              } else {
                formattedValue = formattedValue.slice(0, cursorBefore) + '0' + formattedValue.slice(cursorBefore + 1);
                newCursor = cursorBefore;
              }
            }
          }
          //ensure it stays in the proper bounds
          newCursor = Math.max(0, Math.min(formattedValue.length, newCursor));

          return {
            value: formattedValue,
            selection: [newCursor, newCursor],
          };
        },
      ],
    };
  }

  private getMaxDaysInMonth(month: number, year: number): number {
    switch (month) {
      case 2: //febuary
        return this.isLeapYear(year) ? 29 : 28;
      case 4: case 6: case 9: case 11: //april, june, september, november
        return 30;
      default: //january, march, may, july, august, october, december
        return 31;
    }
  }

  private isLeapYear(year: number): boolean {
    //leap year rule: a year is a leap year if it is evenly divisible by four, except for years that are also divisible by 100 but not by 400
    return ((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0);
  }

  private getMaxNumberValue(maxLength: number, decimalsLength: number): number {
    const pictureLength = maxLength - decimalsLength;
    //console.log('creating max number value for clarion picture - its length will be ', pictureLength);
    return Math.pow(10, pictureLength);
  }

}